import { useRef, useState, useCallback, useEffect, useLayoutEffect } from 'react';
import { getElementVisibilityWithinContainer, ITab } from '../Home.service';
import CaseTab from '../CaseTab/CaseTab';
import styles from './CaseTabs.module.scss';
import MeatballDropdown from '../MeatballDropdown/MeatballDropdown';

interface ICaseTabs {
  tabs: ITab[];
  selectedTabId?: number;
  onTabSelect: (id: number) => void;
}

const CaseTabs = (props: ICaseTabs) => {
  const { tabs, selectedTabId, onTabSelect } = props;
  const containerRef = useRef<HTMLDivElement>(null);
  const tabsRef = useRef<HTMLDivElement>(null);
  const [displayMeatballIcon, setDisplayMeatballIcon] = useState(false);
  const [visibleTabsCount, setVisibleTabsCount] = useState(0);
  const [displayArrow, setDisplayArrow] = useState(false);

  useEffect(() => {
    const updateTabs = () => {
      if (selectedTabId && tabs.length > 0) {
        const containerLeft = containerRef?.current?.getBoundingClientRect().left as number;
        const tabsLeft = tabsRef?.current?.getBoundingClientRect().left as number;
        if (tabsLeft - containerLeft === 0) {
          updateVisibleTabsCount();
        } else {
          setTimeout(() => updateTabsPosition(selectedTabId));
        }
      }
    };

    updateTabs();

    window.addEventListener('resize', updateTabs);
    return () => window.removeEventListener('resize', updateTabs);
  }, [selectedTabId, tabs]);

  useLayoutEffect(() => {
    const handleScroll = () => {
      setDisplayArrow((containerRef?.current?.scrollLeft as number) > 0);
      updateVisibleTabsCount();
    };

    const container = containerRef.current;
    container?.addEventListener('scroll', handleScroll);
    return () => container?.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleWheel = (event: WheelEvent) => {
      event.preventDefault();
      containerRef.current && (containerRef.current.scrollLeft += event.deltaY);
    };

    const container = containerRef.current;
    container?.addEventListener('wheel', handleWheel);
    return () => container?.removeEventListener('wheel', handleWheel);
  }, []);

  const updateVisibleTabsCount = useCallback(() => {
    const container = containerRef.current as HTMLDivElement;
    const tabsArray = Array.from(tabsRef?.current?.children as HTMLCollection);

    let count = 0;
    let isTabsOnLeft = true;

    for (let index = 0; index < tabsArray.length; index++) {
      const { fullyVisible, partiallyVisibleFromRight } = getElementVisibilityWithinContainer(
        container,
        tabsArray[index] as HTMLDivElement
      );

      if (fullyVisible || partiallyVisibleFromRight) isTabsOnLeft = false;
      if (fullyVisible || isTabsOnLeft) count++;
    }

    setVisibleTabsCount(count);
    setDisplayMeatballIcon(count !== tabsArray.length);
  }, []);

  const updateTabsPosition = (tabId: number) => {
    const tabIndex = tabs.findIndex(({ id }) => id === tabId);
    const tabsElement = tabsRef?.current?.querySelectorAll('[class*="case-tab__container"]');

    if (!tabsElement) return;

    const { fullyVisible, partiallyVisibleFromLeft } = getElementVisibilityWithinContainer(
      containerRef.current as HTMLDivElement,
      tabsElement[tabIndex] as HTMLDivElement
    );

    if (!fullyVisible) {
      const refWidth = Array.from(tabsElement)
        .slice(0, partiallyVisibleFromLeft ? tabIndex : tabIndex + 1)
        .reduce(
          (acc, ele, index, list) => acc + (ele as HTMLDivElement).offsetWidth + (index < list.length ? 4 : 0),
          0
        );

      setDisplayArrow(
        refWidth - (containerRef?.current?.offsetWidth as number) > 0 || (partiallyVisibleFromLeft && tabIndex !== 0)
      );

      if (!partiallyVisibleFromLeft) {
        const newVisibleCount = tabIndex + 1;
        setVisibleTabsCount(newVisibleCount);
        setDisplayMeatballIcon(newVisibleCount !== tabs.length);
      }

      requestAnimationFrame(() => {
        setTimeout(() => {
          const ele = containerRef.current;
          if (!ele) return;

          let width = refWidth - (partiallyVisibleFromLeft ? 0 : ele.offsetWidth);
          const currentTab = tabsElement[tabIndex] as HTMLDivElement;

          if (ele.offsetWidth <= currentTab.offsetWidth) {
            width = partiallyVisibleFromLeft ? refWidth : refWidth - currentTab.offsetWidth;
          }

          ele.scrollTo({ left: Math.max(0, width), behavior: 'smooth' });

          if (partiallyVisibleFromLeft) {
            setTimeout(() => updateVisibleTabsCount(), 1000);
          }
        });
      });
    }
  };

  const resetTabsPosition = () => {
    setDisplayArrow(false);
    containerRef.current && containerRef.current.scrollTo({ left: 0, behavior: 'smooth' });
    setTimeout(() => updateVisibleTabsCount(), 1000);
  };

  const handleCaseTabChange = (tabId: number) => {
    if ((tabsRef?.current?.offsetWidth as number) > 0) updateTabsPosition(tabId);
    onTabSelect(tabId);
  };

  const casesInDropdown = tabs.slice(visibleTabsCount);
  const shouldHighlightMeatball = casesInDropdown.some(({ id }) => id === selectedTabId);

  return (
    <div className={styles['case-tabs']}>
      {displayArrow && <div className={styles['case-tabs__arrow--left']} onClick={resetTabsPosition}></div>}
      <div
        className={`${styles['case-tabs__container']} ${displayArrow ? styles['case-tabs__container--moved'] : ''}`}
        ref={containerRef}
      >
        <div ref={tabsRef} className={styles['case-tabs__list']}>
          {tabs.map((tab, index) => {
            const { id, name, userIcon } = tab;
            return (
              <CaseTab
                key={index}
                id={id}
                title={name}
                userIcon={userIcon}
                onClickTab={(id) => handleCaseTabChange(id)}
                isSelected={id === selectedTabId}
              />
            );
          })}
        </div>
      </div>
      {displayMeatballIcon && (
        <div className={styles['case-tabs__dropdown']}>
          <MeatballDropdown
            items={casesInDropdown}
            shouldHighlightMeatball={shouldHighlightMeatball}
            currentSelectedItemId={selectedTabId}
            onSelectItem={(id) => handleCaseTabChange(id)}
          />
        </div>
      )}
    </div>
  );
};

export default CaseTabs;
